import { call, put, select } from "redux-saga/effects";
import Swal from "sweetalert2";
import {
    API_BASE_URL,
    deleteRequest,
    extractErrorMessage,
    getRequest,
    patchRequest,
    postRequest,
    putRequest,
} from "../../../api";
import {
    changeKmsContentStatusFail,
    changeKmsContentStatusSuccess,
    createKmsContentCommentFail,
    createKmsContentCommentSuccess,
    createKmsContentFail,
    createKmsContentSuccess,
    deleteKmsContentCommentFail,
    deleteKmsContentCommentSuccess,
    deleteKmsContentFail,
    deleteKmsContentSuccess,
    generateKmsArticleDraftFail,
    generateKmsArticleDraftSuccess,
    getKmsArchivedContentsFail,
    getKmsArchivedContentsSuccess,
    getKmsContentsFail,
    getKmsContentsSuccess,
    getKmsContentCommentsFail,
    getKmsContentCommentsSuccess,
    getKmsContentRatingSummaryFail,
    getKmsContentRatingSummarySuccess,
    getKmsContentSubscriptionFail,
    getKmsContentSubscriptionSuccess,
    getKmsContentFail,
    getKmsContentSuccess,
    getKmsContentsByRepertoireFail,
    getKmsContentsByRepertoireSuccess,
    getKmsContentWorkflowHistoryFail,
    getKmsContentWorkflowHistorySuccess,
    getKmsTrashedContentsFail,
    getKmsTrashedContentsSuccess,
    hardDeleteKmsContentFail,
    hardDeleteKmsContentSuccess,
    restoreKmsContentFromArchiveFail,
    restoreKmsContentFromArchiveSuccess,
    restoreKmsContentFromTrashFail,
    restoreKmsContentFromTrashSuccess,
    setKmsContentDetailLoading,
    setKmsContentListLoading,
    setKmsContentSaveLoading,
    subscribeKmsContentFail,
    subscribeKmsContentSuccess,
    unsubscribeKmsContentFail,
    unsubscribeKmsContentSuccess,
    upsertKmsContentRatingFail,
    upsertKmsContentRatingSuccess,
    updateKmsContentFail,
    updateKmsContentSuccess,
} from "../actions/kmsContentActions";

const getUidHeaderAndToken = () => ({
    uid: sessionStorage.getItem("uid") || localStorage.getItem("uid"),
    token: sessionStorage.getItem("token") || localStorage.getItem("token"),
});

const buildWorkflowBody = (comment) => ({ comment: comment || "" });

function* refreshContentCollections(content) {
    if (!content) {
        return;
    }

    const lastFilters = yield select((state) => state.kmsContent?.lastFilters || {});
    if (content.repertoireId) {
        yield put({ type: "GET_KMS_CONTENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId: content.repertoireId } });
    }
    yield put({ type: "GET_KMS_CONTENTS_REQUEST", payload: lastFilters });
    yield put({ type: "GET_KMS_CONTENT_WORKFLOW_HISTORY_REQUEST", payload: { id: content.id } });
}

export function* getKmsContentsByRepertoireSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { repertoireId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/repertoire/${repertoireId}`;

    yield put(setKmsContentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentsByRepertoireSuccess(repertoireId, resp.data));
    } catch (error) {
        yield put(getKmsContentsByRepertoireFail(extractErrorMessage(error, "Erreur de chargement des contenus KMS")));
    } finally {
        yield put(setKmsContentListLoading(false));
    }
}

export function* getKmsContentCommentsSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/comments`;

    yield put({
        type: "SET_KMS_CONTENT_COMMENTS_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentCommentsSuccess(contentId, resp.data));
    } catch (error) {
        yield put(getKmsContentCommentsFail(contentId, extractErrorMessage(error, "Erreur de chargement des commentaires")));
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_COMMENTS_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* getKmsContentSubscriptionSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/subscription`;

    yield put({
        type: "SET_KMS_CONTENT_SUBSCRIPTION_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentSubscriptionSuccess(contentId, resp.data));
    } catch (error) {
        yield put(getKmsContentSubscriptionFail(contentId, extractErrorMessage(error, "Erreur de chargement de l'abonnement")));
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_SUBSCRIPTION_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* getKmsContentRatingSummarySaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/ratings/summary`;

    yield put({
        type: "SET_KMS_CONTENT_RATING_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentRatingSummarySuccess(contentId, resp.data));
    } catch (error) {
        yield put(getKmsContentRatingSummaryFail(contentId, extractErrorMessage(error, "Erreur de chargement des évaluations")));
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_RATING_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* getKmsContentsSaga(action) {
    const { token } = getUidHeaderAndToken();
    const params = new URLSearchParams();
    const filters = action.payload || {};

    if (filters.repertoireId) params.set("repertoireId", filters.repertoireId);
    if (filters.type) params.set("type", filters.type);
    if (filters.status) params.set("status", filters.status);

    const query = params.toString() ? `?${params.toString()}` : "";
    const url = `${API_BASE_URL}/api/kms/contents${query}`;

    yield put(setKmsContentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentsSuccess(resp.data, filters));
    } catch (error) {
        yield put(getKmsContentsFail(extractErrorMessage(error, "Erreur de chargement des contenus KMS")));
    } finally {
        yield put(setKmsContentListLoading(false));
    }
}

export function* getKmsArchivedContentsSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/contents/archive`;

    yield put(setKmsContentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsArchivedContentsSuccess(resp.data));
    } catch (error) {
        yield put(getKmsArchivedContentsFail(extractErrorMessage(error, "Erreur de chargement des contenus archives")));
    } finally {
        yield put(setKmsContentListLoading(false));
    }
}

export function* getKmsTrashedContentsSaga() {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/contents/trash`;

    yield put(setKmsContentListLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsTrashedContentsSuccess(resp.data));
    } catch (error) {
        yield put(getKmsTrashedContentsFail(extractErrorMessage(error, "Erreur de chargement des contenus en corbeille")));
    } finally {
        yield put(setKmsContentListLoading(false));
    }
}

export function* generateKmsArticleDraftSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/contents/article-draft`;

    yield put({ type: "SET_KMS_ARTICLE_DRAFT_LOADING", payload: true });
    try {
        const resp = yield call(postRequest, { url, data: action.payload, token });
        yield put(generateKmsArticleDraftSuccess(resp.data?.draft || ""));
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la generation du brouillon");
        yield put(generateKmsArticleDraftFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({ type: "SET_KMS_ARTICLE_DRAFT_LOADING", payload: false });
    }
}

export function* getKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}`;

    yield put(setKmsContentDetailLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentSuccess(resp.data));
    } catch (error) {
        yield put(getKmsContentFail(extractErrorMessage(error, "Erreur de chargement du contenu")));
    } finally {
        yield put(setKmsContentDetailLoading(false));
    }
}

export function* getKmsContentWorkflowHistorySaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/workflow/history`;

    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getKmsContentWorkflowHistorySuccess(id, resp.data));
    } catch (error) {
        yield put(getKmsContentWorkflowHistoryFail(extractErrorMessage(error, "Erreur de chargement de l'historique workflow")));
    }
}

export function* createKmsContentCommentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId, message } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/comments`;

    yield put({
        type: "SET_KMS_CONTENT_COMMENT_SAVE_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        yield call(postRequest, { url, data: { message }, token });
        yield put(createKmsContentCommentSuccess(contentId));
        yield put({ type: "GET_KMS_CONTENT_COMMENTS_REQUEST", payload: { contentId } });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'ajout du commentaire");
        yield put(createKmsContentCommentFail(contentId, messageError));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: messageError,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_COMMENT_SAVE_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* subscribeKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/subscription`;

    yield put({
        type: "SET_KMS_CONTENT_SUBSCRIPTION_SAVE_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(postRequest, { url, data: {}, token });
        yield put(subscribeKmsContentSuccess(contentId, resp.data));
        yield put({ type: "GET_KMS_CONTENT_SUBSCRIPTION_REQUEST", payload: { contentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Abonnement au contenu enregistré",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'abonnement au contenu");
        yield put(subscribeKmsContentFail(contentId, messageError));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: messageError,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_SUBSCRIPTION_SAVE_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* upsertKmsContentRatingSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId, rating } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/ratings`;

    yield put({
        type: "SET_KMS_CONTENT_RATING_SAVE_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(postRequest, { url, data: { rating }, token });
        yield put(upsertKmsContentRatingSuccess(contentId, resp.data));
        yield put({ type: "GET_KMS_CONTENT_RATING_SUMMARY_REQUEST", payload: { contentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Votre évaluation a été enregistrée",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de l'enregistrement de l'évaluation");
        yield put(upsertKmsContentRatingFail(contentId, messageError));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: messageError,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_RATING_SAVE_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* unsubscribeKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/subscription`;

    yield put({
        type: "SET_KMS_CONTENT_SUBSCRIPTION_SAVE_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        const resp = yield call(deleteRequest, { url, token });
        yield put(unsubscribeKmsContentSuccess(contentId, resp.data));
        yield put({ type: "GET_KMS_CONTENT_SUBSCRIPTION_REQUEST", payload: { contentId } });
        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Désabonnement du contenu enregistré",
            timer: 1500,
            showConfirmButton: false,
        });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors du désabonnement du contenu");
        yield put(unsubscribeKmsContentFail(contentId, messageError));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: messageError,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_SUBSCRIPTION_SAVE_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* deleteKmsContentCommentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { contentId, commentId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${contentId}/comments/${commentId}`;

    yield put({
        type: "SET_KMS_CONTENT_COMMENT_SAVE_LOADING",
        payload: { contentId, loading: true },
    });
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteKmsContentCommentSuccess(contentId));
        yield put({ type: "GET_KMS_CONTENT_COMMENTS_REQUEST", payload: { contentId } });
    } catch (error) {
        const messageError = extractErrorMessage(error, "Erreur lors de la suppression du commentaire");
        yield put(deleteKmsContentCommentFail(contentId, messageError));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: messageError,
            confirmButtonText: "OK",
        });
    } finally {
        yield put({
            type: "SET_KMS_CONTENT_COMMENT_SAVE_LOADING",
            payload: { contentId, loading: false },
        });
    }
}

export function* createKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const url = `${API_BASE_URL}/api/kms/contents`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(postRequest, { url, data: action.payload, token });
        yield put(createKmsContentSuccess(resp.data));
        yield* refreshContentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu KMS cree avec succes",
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la creation du contenu");
        yield put(createKmsContentFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* updateKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id, data } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data, token });
        yield put(updateKmsContentSuccess(resp.data));
        yield* refreshContentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu KMS mis a jour avec succes",
            timer: 1800,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la mise a jour du contenu");
        yield put(updateKmsContentFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* changeKmsContentStatusSaga(action) {
    const { uid, token } = getUidHeaderAndToken();
    const { id, status } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/status?status=${status}`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(patchRequest, { url, data: {}, uid, token });
        yield put(changeKmsContentStatusSuccess(resp.data));
        yield* refreshContentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Statut du contenu mis a jour",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors du changement de statut");
        yield put(changeKmsContentStatusFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* executeKmsContentWorkflowSaga(action, endpoint, successMessage, defaultErrorMessage) {
    const { token } = getUidHeaderAndToken();
    const { id, comment } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/workflow/${endpoint}`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(postRequest, { url, data: buildWorkflowBody(comment), token });
        yield put(changeKmsContentStatusSuccess(resp.data));
        yield* refreshContentCollections(resp.data);

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: successMessage,
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, defaultErrorMessage);
        yield put(changeKmsContentStatusFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* submitKmsContentSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "submit",
        "Contenu soumis pour validation",
        "Erreur lors de la soumission du contenu"
    );
}

export function* approveKmsContentSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "approve",
        "Contenu approuve avec succes",
        "Erreur lors de l'approbation du contenu"
    );
}

export function* rejectKmsContentSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "reject",
        "Contenu rejete avec succes",
        "Erreur lors du rejet du contenu"
    );
}

export function* publishKmsContentSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "publish",
        "Contenu publié avec succes",
        "Erreur lors de la publication du contenu"
    );
}

export function* archiveKmsContentSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "archive",
        "Contenu archivé avec succes",
        "Erreur lors de l'archivage du contenu"
    );
}

export function* returnKmsContentToDraftSaga(action) {
    yield* executeKmsContentWorkflowSaga(
        action,
        "return-for-correction",
        "Contenu retourné pour correction",
        "Erreur lors du retour pour correction"
    );
}

export function* deleteKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id, repertoireId } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}`;

    yield put(setKmsContentSaveLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteKmsContentSuccess(id, repertoireId));
        if (repertoireId) {
            yield put({ type: "GET_KMS_CONTENTS_BY_REPERTOIRE_REQUEST", payload: { repertoireId } });
        }

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu archivé avec succes",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de l'archivage du contenu");
        yield put(deleteKmsContentFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* restoreKmsContentFromArchiveSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/restore-archive`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data: {}, token });
        yield put(restoreKmsContentFromArchiveSuccess(resp.data));
        yield put({ type: "GET_KMS_ARCHIVED_CONTENTS_REQUEST" });
        yield put({ type: "GET_KMS_CONTENTS_REQUEST", payload: {} });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu restauré depuis les archives",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la restauration du contenu archive");
        yield put(restoreKmsContentFromArchiveFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* restoreKmsContentFromTrashSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/restore-trash`;

    yield put(setKmsContentSaveLoading(true));
    try {
        const resp = yield call(putRequest, { url, data: {}, token });
        yield put(restoreKmsContentFromTrashSuccess(resp.data));
        yield put({ type: "GET_KMS_TRASHED_CONTENTS_REQUEST" });
        yield put({ type: "GET_KMS_CONTENTS_REQUEST", payload: {} });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu restauré depuis la corbeille",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la restauration du contenu supprime");
        yield put(restoreKmsContentFromTrashFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}

export function* hardDeleteKmsContentSaga(action) {
    const { token } = getUidHeaderAndToken();
    const { id, source } = action.payload;
    const url = `${API_BASE_URL}/api/kms/contents/${id}/hard-delete`;

    yield put(setKmsContentSaveLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(hardDeleteKmsContentSuccess(id, source));
        yield put({ type: source === "archive" ? "GET_KMS_ARCHIVED_CONTENTS_REQUEST" : "GET_KMS_TRASHED_CONTENTS_REQUEST" });

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Contenu supprime definitivement",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur lors de la suppression definitive du contenu");
        yield put(hardDeleteKmsContentFail(message));
        yield call([Swal, Swal.fire], {
            icon: "error",
            text: message,
            confirmButtonText: "OK",
        });
    } finally {
        yield put(setKmsContentSaveLoading(false));
    }
}
